import { useMemo } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import AppNavTree from "@/components/Core/AppNavTree";
import LoadingIndicator from "@/components/Shared/LoadingIndicator/LoadingIndicator";
import { routes } from "@/configs/constants/routes";
import { useOrganisation } from "@/networking/organisation.hooks";
import SettingsLanding from "./SettingsLanding";
import { settingsRoutes, settingsTree } from "./settingsNav";

/**
 * Split-view settings shell: a persistent, searchable `AppNavTree` on the left
 * (rendered outside the routed content so its expand/scroll state survives
 * navigation) and the selected leaf page on the right. Replaces the old row of
 * pill tabs (`TabGroup`).
 */
const SettingsLayout = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const { data: organisation, isLoading } = useOrganisation();

	// Selection is derived from the URL: the first path segment after /settings.
	const selectedId = location.pathname.replace(/^\/settings\/?/, "").split("/")[0] || undefined;

	// Nav and routes are both derived from the same org-filtered node list, so a
	// leaf this organisation may not see has no route either — its URL falls
	// through to the catch-all landing page.
	const tree = useMemo(() => settingsTree(organisation), [organisation]);
	const leafRoutes = useMemo(() => settingsRoutes(organisation), [organisation]);

	return (
		<div className="flex items-start gap-6">
			{/* Bordered card around the whole tree (search + nodes), sized to its
			    content and sticky so it stays put while the right pane scrolls. The
			    tree caps at the viewport and scrolls internally only if it's tall. */}
			<aside className="sticky top-4 w-64 shrink-0 self-start rounded-lg border border-gray-200 bg-white p-3">
				<AppNavTree
					nodes={tree}
					selectedId={selectedId}
					onSelect={(id) => navigate(`${routes.SETTINGS}/${id}`)}
					searchPlaceholder="Sök inställningar…"
					emptyLabel="Inga träffar"
					skipTranslation
					treeClassName="max-h-[calc(100vh-9rem)]"
				/>
			</aside>

			<div className="min-w-0 flex-1">
				{isLoading ? (
					<LoadingIndicator />
				) : (
					<Routes>
						<Route index element={<SettingsLanding />} />
						{leafRoutes.map((route) => (
							<Route key={route.path} path={route.path} element={route.element} />
						))}
						<Route path="*" element={<SettingsLanding />} />
					</Routes>
				)}
			</div>
		</div>
	);
};

export default SettingsLayout;
